import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import type { ChecklistTemplate, ChecklistTemplateBody } from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import {
  allChecklistTemplatesOptions,
  errorMessage,
  type ChecklistTemplateListItem,
} from '@/features/desks/desks-queries';
import { ChecklistTemplateDialog } from '@/features/desks/checklist-template-dialog';
import { DeskRowAction } from '@/features/desks/desk-stages-section';
import { normalizeText, SettingsBreadcrumb, SettingsSearch } from '@/features/settings/settings-ui';

/**
 * /settings/desks/checklists — templates de checklist de TODAS as mesas:
 * tabela Nome | Mesa | itens, switch de ativo e editar/excluir. A ordem é a
 * do servidor (mesa asc); a busca só filtra.
 */
export function ChecklistsSettingsPage() {
  const queryClient = useQueryClient();
  const templates = useQuery(allChecklistTemplatesOptions());
  const [query, setQuery] = useState('');
  const [dialog, setDialog] = useState<{ template: ChecklistTemplateListItem | null } | null>(
    null
  );

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['erp', 'checklists'] });

  const toggle = useMutation({
    mutationFn: async (t: ChecklistTemplateListItem) => {
      // Linha FRESCA antes do PUT: o corpo SUBSTITUI os itens — um body
      // montado do cache apagaria itens adicionados por outro usuário.
      const rows = await queryClient.fetchQuery({
        ...allChecklistTemplatesOptions(),
        staleTime: 0,
      });
      const fresh = rows.find((row) => row.id === t.id);
      if (!fresh) throw new Error('Checklist não encontrado — atualize a lista.');
      const body: ChecklistTemplateBody = {
        deskId: fresh.deskId,
        name: fresh.name,
        active: !t.active,
        items: [...fresh.items].sort((a, b) => a.position - b.position).map((i) => i.text),
      };
      const res = await api<ChecklistTemplate>(`/checklists/${t.id}`, { method: 'PUT', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (templateId: number) => {
      const res = await api<void>(`/checklists/${templateId}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: invalidate,
  });

  // Haystack normalizado pré-computado por linha — a busca só filtra, sem
  // re-sortear (a ordem vem do servidor).
  const rows = useMemo(
    () =>
      (templates.data ?? []).map((t) => ({
        row: t,
        haystack: normalizeText(`${t.name} ${t.deskName}`),
      })),
    [templates.data]
  );
  const q = normalizeText(query.trim());
  const list = useMemo(
    () => (q === '' ? rows : rows.filter((r) => r.haystack.includes(q))).map((r) => r.row),
    [rows, q]
  );

  return (
    <div className="rounded-lg border bg-card p-4 shadow-xs sm:p-5">
      <SettingsBreadcrumb page="Checklists" />
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <h1 className="text-xl leading-7 font-semibold">Checklists</h1>
        <div className="ml-auto flex items-center gap-2">
          <SettingsSearch value={query} onChange={setQuery} label="Buscar checklist" />
          <Button size="sm" className="h-9" onClick={() => setDialog({ template: null })}>
            <Plus className="size-4" strokeWidth={2.5} />
            Checklist
          </Button>
        </div>
      </div>

      <div className="mt-4">
        {templates.isPending ? (
          <div className="space-y-2" aria-busy>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : templates.isError ? (
          <EmptyState
            message={`Erro ao carregar checklists: ${errorMessage(templates.error)}`}
          />
        ) : list.length === 0 ? (
          <EmptyState
            className="h-52"
            message={q !== '' ? 'Nenhum checklist encontrado' : 'Nenhum checklist cadastrado'}
          />
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted text-left text-[13px]">
                  <th className="h-10 px-4 font-semibold">Nome</th>
                  <th className="h-10 px-4 font-semibold">Mesa</th>
                  <th className="h-10 px-4 font-semibold">Itens</th>
                  <th className="h-10 w-32 px-4">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map((t) => (
                  <tr
                    key={t.id}
                    className="h-11 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
                  >
                    <td className="max-w-80 truncate px-4 font-medium">{t.name}</td>
                    <td className="max-w-56 truncate px-4">{t.deskName}</td>
                    <td className="px-4 text-muted-foreground tabular-nums whitespace-nowrap">
                      {t.items.length === 1 ? '1 item' : `${t.items.length} itens`}
                    </td>
                    <td className="px-4">
                      <div className="flex items-center justify-end gap-1.5">
                        <Switch
                          checked={t.active}
                          disabled={toggle.isPending}
                          onCheckedChange={() => toggle.mutate(t)}
                          aria-label={t.active ? `Desativar "${t.name}"` : `Ativar "${t.name}"`}
                        />
                        <DeskRowAction
                          label="Editar checklist"
                          onClick={() => setDialog({ template: t })}
                        >
                          <Pencil className="size-3.5" />
                        </DeskRowAction>
                        <DeskRowAction
                          label="Excluir checklist"
                          destructive
                          disabled={remove.isPending}
                          onClick={() => {
                            if (window.confirm(`Excluir o checklist "${t.name}"?`)) {
                              remove.mutate(t.id);
                            }
                          }}
                        >
                          <Trash2 className="size-3.5" />
                        </DeskRowAction>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {(toggle.error ?? remove.error) ? (
        <p className="mt-2 text-sm text-destructive">
          {errorMessage(toggle.error ?? remove.error)}
        </p>
      ) : null}

      {dialog ? (
        <ChecklistTemplateDialog template={dialog.template} onClose={() => setDialog(null)} />
      ) : null}
    </div>
  );
}
